import { useState } from 'react';

import { Calendar2Line } from '@assets/icons';
import {
  DateType,
  getCurrentDate,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import ResponsiveSearchDrawerItem from '@components/views/components/listing/drawer/ResponsiveSearchDrawerItem';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import ResponsiveSearchInputDrawer from '@components/views/components/listing/drawer/ResponsiveSearchInputDrawer/ResponsiveSearchInputDrawer';
import { FlightSearchInputDrawerType } from '@components/views/flight/types';
import ResponsiveDateRangePicker from '@components/DatePicker/DateRangePicker/ResponsiveDateRangePicker';
import { DayPickerRangeControllerProps } from '@components/DatePicker/DateRangePicker/DateRangePicker';
import { useCalendarHoliday } from '@components/DatePicker/DateRangePicker/useCalendarHoliday';
import { getDateRangePickerPlaceholder } from '@components/views/flight/listing/SearchBar/DateSelect/utils';
import { SearchBarPopoverProps } from '@components/ui/search/SearchBarInput/SearchBarInputInterface';

import {
  DateSelectionDrawerFooter,
  HotelMobileHolidayPanelLayout,
  ResponsiveHolidayFooterDot,
  ResponsiveHolidayFooterItem,
  ResponsiveHolidayFooterSummary,
} from './styled';

const searchLabel = 'Giriş-Çıkış Tarihiniz?';

const ResponsiveDateRangeSelectDrawer = ({
  startDate,
  endDate,
  onDatesChange,
  isTravelPlanning = false,
  title,
  subTitle,
  placeholder,
  drawerTitle,
  arrowIcon,
  inputComponent,
  isSubmit,
  popoverProps,
  showLabel = false,
}: Pick<
  DayPickerRangeControllerProps,
  'startDate' | 'endDate' | 'onDatesChange'
> &
  FlightSearchInputDrawerType & {
    inputComponent:
      | 'shopAndFlyHomePageFlight'
      | 'shopAndFlyHomePageHotel'
      | 'setur';
  } & { isSubmit?: boolean; isHomePage?: boolean } & {
    popoverProps?: SearchBarPopoverProps;
    showLabel?: boolean;
    isTravelPlanning?: boolean;
  }) => {
  return (
    <ResponsiveSearchInputDrawer
      icon={Calendar2Line}
      title={title}
      subTitle={subTitle}
      placeholder={placeholder}
      drawerTitle={drawerTitle}
      arrowIcon={arrowIcon}
      inputComponent={inputComponent}
      startDate={startDate}
      endDate={endDate}
      isSubmit={isSubmit}
      popoverProps={popoverProps}
      showLabel={showLabel}
      footerCanGrow
    >
      {(hideModal) => (
        <ResponsiveDateRangeSelect
          startDate={startDate}
          endDate={endDate}
          onDatesChange={onDatesChange}
          isTravelPlanning={isTravelPlanning}
          hideModal={hideModal}
        />
      )}
    </ResponsiveSearchInputDrawer>
  );
};

const ResponsiveDateRangeSelect = ({
  startDate,
  endDate,
  onDatesChange,
  isTravelPlanning = false,
  hideModal,
}: Pick<
  DayPickerRangeControllerProps,
  'startDate' | 'endDate' | 'onDatesChange'
> & {
  isTravelPlanning?: boolean;
  hideModal: () => void;
}) => {
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(
    startDate
  );
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(endDate);
  const [visibleHolidayNames, setVisibleHolidayNames] = useState<string[]>([]);
  const isHolidayEnabled = !isTravelPlanning;
  // MAP-takvim-guncelleme: mobile drawer for the HOTEL search bar. Shares the
  // cached ['calendarHolidays'] query with the desktop path.
  const { data: holidayData } = useCalendarHoliday();

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  };

  const localSubTitle = getDateRangePickerPlaceholder(
    localStartDate,
    localEndDate
  );

  const onSubmit = () => {
    if (localStartDate && localEndDate) {
      onDatesChange({ startDate: localStartDate, endDate: localEndDate });
      hideModal();
    }
  };

  return (
    <>
      <ResponsiveSearchDrawerItem
        icon={Calendar2Line}
        title={searchLabel}
        subTitle={localSubTitle}
        placeholder='Giriş - Çıkış Tarihleri'
      />

      <HotelMobileHolidayPanelLayout>
        <ResponsiveDateRangePicker
          minimumNights={0}
          startDate={localStartDate}
          endDate={localEndDate}
          onDatesChange={handleDatesChange}
          onVisibleHolidayNamesChange={setVisibleHolidayNames}
          holidayPanelFillsAvailableSpace
          isDynamicFocus
          isOutsideRange={(day) =>
            !isInclusivelyAfterDay(day, getCurrentDate())
          }
          initialData={isHolidayEnabled ? holidayData : undefined}
          isHoliday={isHolidayEnabled}
        />
      </HotelMobileHolidayPanelLayout>

      <DateSelectionDrawerFooter>
        {isHolidayEnabled && visibleHolidayNames.length > 0 ? (
          <ResponsiveHolidayFooterSummary>
            {visibleHolidayNames.map((holidayName) => (
              <ResponsiveHolidayFooterItem key={holidayName}>
                <ResponsiveHolidayFooterDot />
                {holidayName}
              </ResponsiveHolidayFooterItem>
            ))}
          </ResponsiveHolidayFooterSummary>
        ) : null}
        <ResponsiveApplyButton onClick={onSubmit} />
      </DateSelectionDrawerFooter>
    </>
  );
};

export default ResponsiveDateRangeSelectDrawer;
